import React from 'react';
import { 
  Target, 
  Heart, 
  AlertTriangle, 
  Lightbulb, 
  CheckCircle2,
  Shield
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface Challenge {
  id: string;
  one_liner: string | null;
  problem_description: string | null;
  who_it_affects: string | null;
  why_it_matters: string | null;
  opportunity: string | null;
  solution_directions: string[] | null;
  inputs_dependencies: string | null;
  risks_guardrails: string | null;
  success_metrics: string | null;
  references_contacts: string | null;
}

interface ChallengeBriefSectionProps {
  challenge: Challenge;
  causeName: string;
}

// Helper to truncate content to first 2-3 sentences
const truncateContent = (content: string, maxSentences: number = 3): string => {
  const sentences = content.match(/[^.!?]+[.!?]+/g) || [content];
  return sentences.slice(0, maxSentences).join(' ').trim();
};

// Component to render content with proper bullet list formatting
const FormattedContent: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let currentBullets: string[] = [];
  let paragraphBuffer = '';

  const flushParagraph = () => {
    if (paragraphBuffer.trim()) {
      elements.push(
        <p key={`p-${elements.length}`} className="text-muted-foreground leading-relaxed mb-3">
          {paragraphBuffer.trim()}
        </p>
      );
      paragraphBuffer = '';
    }
  };

  const flushBullets = () => {
    if (currentBullets.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="list-none space-y-2 pl-1 mb-3">
          {currentBullets.map((bullet, idx) => (
            <li key={idx} className="flex items-start gap-2 text-muted-foreground leading-relaxed">
              <CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mt-0.5" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      );
      currentBullets = [];
    }
  };

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    const bulletMatch = trimmedLine.match(/^[-•*]\s*(.+)/);
    
    if (bulletMatch) {
      flushParagraph();
      currentBullets.push(bulletMatch[1]);
    } else if (trimmedLine) {
      flushBullets();
      paragraphBuffer += (paragraphBuffer ? ' ' : '') + trimmedLine;
    }
  });

  flushParagraph();
  flushBullets();

  return <div>{elements}</div>;
};

const ChallengeBriefSection: React.FC<ChallengeBriefSectionProps> = ({ 
  challenge,
  causeName 
}) => {
  // Get first few solution directions
  const displayedDirections = challenge.solution_directions?.slice(0, 4) || [];
  const remainingCount = (challenge.solution_directions?.length || 0) - 4;

  return (
    <section className="space-y-6 print:break-inside-avoid print:break-before-page">
      <div>
        <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">
          The Challenge
        </p>
        <h2 className="text-2xl md:text-3xl font-bold font-display">
          Building for {causeName}
        </h2>
      </div>

      {/* One-liner Callout */}
      {challenge.one_liner && (
        <Card className="bg-primary/5 border-l-4 border-l-primary border-t-0 border-r-0 border-b-0 rounded-l-none">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-lg font-medium">{challenge.one_liner}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Context Card - Merged Why It Matters & The Problem */}
      {(challenge.why_it_matters || challenge.problem_description) && (
        <Card className="bg-card border-border">
          <CardContent className="p-5 space-y-4">
            {challenge.why_it_matters && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Why It Matters</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  {truncateContent(challenge.why_it_matters)}
                </p>
              </div>
            )}
            {challenge.problem_description && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">The Problem</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  {truncateContent(challenge.problem_description)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* What Could Be Built - with proper formatting */}
      {challenge.opportunity && (
        <Card className="bg-secondary/5 border-secondary/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="h-5 w-5 text-secondary" />
              <h3 className="font-semibold font-display">What Could Be Built</h3>
            </div>
            <FormattedContent content={challenge.opportunity} />
          </CardContent>
        </Card>
      )}

      {/* Solution Directions - Compact Chips */}
      {displayedDirections.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Solution Directions
          </h3>
          <div className="flex flex-wrap gap-2">
            {displayedDirections.map((direction, index) => (
              <Badge 
                key={index} 
                variant="outline" 
                className="py-1.5 px-3 text-sm font-normal bg-muted/30"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-secondary mr-1.5 shrink-0" />
                {direction}
              </Badge>
            ))}
            {remainingCount > 0 && (
              <Badge variant="secondary" className="py-1.5 px-3 text-sm font-normal">
                +{remainingCount} more
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Expandable Additional Details */}
      {(challenge.success_metrics || challenge.risks_guardrails || challenge.who_it_affects) && (
        <Accordion type="single" collapsible className="w-full print:hidden">
          {challenge.success_metrics && (
            <AccordionItem value="success">
              <AccordionTrigger className="hover:no-underline text-sm">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  What Success Looks Like
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="pl-6 text-muted-foreground leading-relaxed">
                  {challenge.success_metrics}
                </p>
              </AccordionContent>
            </AccordionItem>
          )}

          {challenge.risks_guardrails && (
            <AccordionItem value="risks">
              <AccordionTrigger className="hover:no-underline text-sm">
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  Risks & Guardrails
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="pl-6 text-muted-foreground leading-relaxed">
                  {challenge.risks_guardrails}
                </p>
              </AccordionContent>
            </AccordionItem>
          )}

          {challenge.who_it_affects && (
            <AccordionItem value="audience">
              <AccordionTrigger className="hover:no-underline text-sm">
                <span className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-primary" />
                  Who It Affects
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="pl-6 text-muted-foreground leading-relaxed">
                  {challenge.who_it_affects}
                </p>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}

      {/* Print-only: Show all details */}
      <div className="hidden print:block space-y-4">
        {challenge.success_metrics && (
          <div>
            <h4 className="font-semibold text-sm mb-2">What Success Looks Like</h4>
            <p className="text-muted-foreground">{challenge.success_metrics}</p>
          </div>
        )}
        {challenge.risks_guardrails && (
          <div>
            <h4 className="font-semibold text-sm mb-2">Risks & Guardrails</h4>
            <p className="text-muted-foreground">{challenge.risks_guardrails}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ChallengeBriefSection;